/* global axios */
<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRouter, useRoute } from 'vue-router';
import { useEmitter } from 'dashboard/composables/emitter';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();
const currentAccountId = useMapGetter('getCurrentAccountId');

const hasActiveCall = useMapGetter('whatsappCall/hasActiveCall');
const activeCall = useMapGetter('whatsappCall/activeCall');
const isMuted = useMapGetter('whatsappCall/isMuted');
const callDuration = useMapGetter('whatsappCall/callDuration');
const currentConversationId = computed(() => {
  const route = router.currentRoute.value;
  return route.params.conversationId ? Number(route.params.conversationId) : null;
});

// Listen for message updates to detect when call ends
const handleMessageUpdate = (message) => {
  // Only handle voice_call messages
  if (message.content_type !== 'voice_call') return;

  // Check if this message is for the active call
  if (!activeCall.value) return;

  const callData = message.content_attributes?.data;
  if (!callData || callData.call_id !== activeCall.value.callId) return;

  // If call status changed to completed/rejected/no-answer, end the call
  const status = callData.status;
  if (status === 'completed' || status === 'rejected' || status === 'no-answer') {
    console.log('[WhatsApp Call] Call ended by user, clearing active call:', status);
    store.dispatch('whatsappCall/endCall');
  }
};

useEmitter('message.updated', handleMessageUpdate);

// Hide floating widget if viewing the conversation with the active call
const shouldShowFloating = computed(() => {
  if (!hasActiveCall.value) return false;
  if (!activeCall.value) return false;
  // Hide if we're viewing the conversation with the active call
  return currentConversationId.value !== activeCall.value.conversationId;
});

const formattedDuration = computed(() => {
  const seconds = callDuration.value;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
});

const toggleMute = () => {
  // Get the peer connection
  const pc = store.state.whatsappCall.janusPluginHandle;
  if (!pc) return;

  // Get current mute state BEFORE toggling in store
  const currentlyMuted = isMuted.value;

  // Toggle mute on all audio tracks
  const senders = pc.getSenders();
  senders.forEach(sender => {
    if (sender.track && sender.track.kind === 'audio') {
      sender.track.enabled = currentlyMuted; // If muted, enable (unmute). If not muted, disable (mute)
      console.log('[WhatsApp Call] Audio track', currentlyMuted ? 'unmuted' : 'muted');
    }
  });

  // Update store state
  store.dispatch('whatsappCall/toggleMute');
};

const endCall = async () => {
  const callId = activeCall.value?.callId;

  if (callId) {
    try {
      // Send terminate request to WhatsApp API
      const accountId = currentAccountId.value;
      await axios.post(`/api/v1/accounts/${accountId}/whatsapp_calls/terminate`, {
        call_id: callId
      });

      console.log('[WhatsApp Call] Successfully terminated call');
    } catch (error) {
      console.error('[WhatsApp Call] Failed to terminate call:', error);
    }
  }

  // Clear local call state
  await store.dispatch('whatsappCall/endCall');
};

const navigateToConversation = () => {
  const conversationId = activeCall.value?.conversationId;
  const accountId = route.params.accountId;

  if (conversationId && accountId) {
    const path = frontendURL(
      conversationUrl({
        accountId,
        id: conversationId,
      })
    );
    router.push({ path });
  }
};

// Duration timer
let durationInterval = null;

watch(hasActiveCall, (active) => {
  if (active) {
    // Start duration counter
    durationInterval = setInterval(() => {
      store.dispatch('whatsappCall/updateDuration', callDuration.value + 1);
    }, 1000);
  } else {
    // Clear duration counter
    if (durationInterval) {
      clearInterval(durationInterval);
      durationInterval = null;
    }
  }
}, { immediate: true });

onUnmounted(() => {
  if (durationInterval) {
    clearInterval(durationInterval);
  }
});
</script>

<template>
  <transition name="slide-up">
    <div
      v-if="shouldShowFloating"
      class="fixed bottom-4 right-4 z-40 bg-white rounded-lg shadow-2xl border border-n-slate-6 w-80 overflow-hidden"
    >
      <!-- Header with caller info -->
      <div
        class="bg-n-teal-9 text-white p-3 flex items-center gap-3 cursor-pointer hover:bg-n-teal-10 transition-colors"
        @click="navigateToConversation"
      >
        <div class="bg-white/20 rounded-full p-2">
          <Icon icon="i-ph-phone" class="size-5" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium truncate">{{ activeCall?.callerName || 'WhatsApp Call' }}</p>
          <p class="text-xs opacity-90">{{ formattedDuration }}</p>
        </div>
        <Icon icon="i-ph-caret-right" class="size-4 opacity-70" />
      </div>

      <!-- Controls -->
      <div class="p-3 bg-n-slate-1 flex items-center justify-around gap-2">
        <!-- Mute button -->
        <button
          class="flex-1 rounded-lg py-2 px-3 flex items-center justify-center gap-2 font-medium transition-colors"
          :class="isMuted ? 'bg-n-ruby-9 text-white' : 'bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4'"
          @click="toggleMute"
        >
          <Icon :icon="isMuted ? 'i-ph-microphone-slash' : 'i-ph-microphone'" class="size-4" />
          <span class="text-xs">{{ isMuted ? 'Unmute' : 'Mute' }}</span>
        </button>

        <!-- End call button -->
        <button
          class="flex-1 bg-n-ruby-9 hover:bg-n-ruby-10 text-white rounded-lg py-2 px-3 flex items-center justify-center gap-2 font-medium transition-colors"
          @click="endCall"
        >
          <Icon icon="i-ph-phone-x" class="size-4" />
          <span class="text-xs">End</span>
        </button>
      </div>

      <!-- Audio waveform animation -->
      <div class="h-1 bg-n-slate-2 overflow-hidden">
        <div class="h-full bg-n-teal-9 animate-pulse" style="width: 60%"></div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

@keyframes pulse {
  0%, 100% {
    width: 30%;
  }
  50% {
    width: 80%;
  }
}

.animate-pulse {
  animation: pulse 2s ease-in-out infinite;
}
</style>
